import { useEffect, useRef, useState } from "react";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import ActionPopover from "../../../../components/popovers/actionPopover";
import { AiOutlinePlus } from "react-icons/ai";
import { GoUpload } from "react-icons/go";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../components/search/APISearchInput";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import DeleteModal from "../../../../components/modals/DeleteModal";
import WorkTimeModalForm from "../modals/WorkTimeModalForm";
import { EmployeeWorkTimeDto } from "../../../../models/employee-work-time.dto";
import {
	useGetEmployeeWorkTime,
	useDeleteEmployeeWorkTime,
} from "../../../../services/employee-work-time.service";
import useUserStore from "../../../../state-management/useUserStore";
import { workTimeColumns, workTimeColumnVisibility } from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";
import UploadEmployeeWorkTime from "../modals/UploadEmployeeWorkTime";

const WorkTimeView = () => {
	const { currentUser, userType } = useUserStore();
	const { currentCompany } = useCompanyStore();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openUploadModal, setOpenUploadModal] = useState(false);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(workTimeColumnVisibility);

	// const { mutate: searchEmployeeWorkTimes } = useGetEmployeeWorkTimeSearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<EmployeeWorkTimeDto>({
		fetchHook: useGetEmployeeWorkTime,
		options: {
			companyId: currentCompany?.id,
			queryMode: "ALL",
		},
	});

	// Fetched data for select fields requiring data from database
	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteEmployeeWorkTime } = useDeleteEmployeeWorkTime();
	const { addNewTableData, reloadTableData } =
		useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteEmployeeWorkTime(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					{/* <APISearchInput 
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={undefined as any}
					/> */}
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
									{
										title: "Date Modified",
										fieldName: "modifiedAt",
									},
								],
							},
							{
								title: "Employee",
								fieldName: "employeeId",
								fieldType: "SELECT_FIELD",
								options: employees?.data || [],
								mapTexts: ["firstName", "lastName"],
							},
							{
								title: "Pay Period",
								fieldName: "payPeriodId",
								fieldType: "SELECT_FIELD",
								options: payPeriods?.data || [],
								mapText: "code",
							},
							{
								title: "Time Unit",
								fieldName: "timeUnit",
								fieldType: "CHECKBOX_LIST",
								options: { HOUR: "HOUR", DAY: "DAY" },
							},
						]}
					/>
					<ManageTableColumns
						columns={workTimeColumns}
						defaultVisibilityModel={workTimeColumnVisibility}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					<button
						onClick={() => setOpenUploadModal(true)}
						className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50"
					>
						<GoUpload className="mr-2 text-[17px] text-gray-500" />
						Upload Work Times
					</button>
					<div
						className="flex items-center text-primary-blue cursor-pointer"
						onClick={handleOpenModal}
					>
						<AiOutlinePlus className="mr-1 text-base" />
						<span className="text-sm">Add New Entry</span>
					</div>
				</div>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.WORK_TIME}
				data={tableData}
				columns={workTimeColumns}
				columnVisibility={columnVisibilityModel}
				onColumnVisibilityChange={setColumnVisibilityModel}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={workTimeColumnVisibility}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={`${formDataValues?.employee?.firstName}'s entry` || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<WorkTimeModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Entry" : "Create Entry"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					employees={employees?.data || []}
					payPeriods={payPeriods?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadModal && (
				<UploadEmployeeWorkTime
					open={openUploadModal}
					setOpen={setOpenUploadModal}
					onUploadSuccess={addNewTableData}
					companyId={currentCompany?.id as number}
				/>
			)}
		</>
	);
};

export default WorkTimeView;
